/**
 * Note Template Info Utilities
 *
 * Abstraction layer for handling template info embedded in note responses.
 *
 * Template format from note detail:
 * - { template_id: string, template_name: string, template_version: number, template_permissions: {...} }
 *
 * Usage:
 * ```typescript
 * import { getTemplateId, getTemplateName } from '@features/templates/utils';
 *
 * // In component:
 * const templateId = getTemplateId(noteData);
 * const templateName = getTemplateName(noteData);
 * ```
 */

/**
 * Normalized template info structure
 */
export interface NormalizedTemplateInfo {
  /** Template ID */
  id: string;
  /** Template name */
  name: string;
  /** Owner scope */
  ownerScope?: 'user' | 'org' | 'public';
}

/**
 * Template format embedded in note detail responses
 */
interface TemplateFormat {
  template_id: string;
  template_name: string;
  owner_scope?: 'user' | 'org' | 'public';
  template_version?: number;
  template_permissions?: {
    can_delete: boolean;
    can_edit: boolean;
    can_fork: boolean;
    can_view: boolean;
  };
}

/**
 * Note data structure that may contain template info
 */
interface NoteDataWithTemplate {
  template?: TemplateFormat | null;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Check if template object is valid
 */
function isValidTemplateFormat(template: unknown): template is TemplateFormat {
  if (!template || typeof template !== 'object') {
    return false;
  }
  const t = template as Record<string, unknown>;
  return typeof t.template_id === 'string' && typeof t.template_name === 'string';
}

/**
 * Check if note data has a template property
 */
function hasTemplateProperty(noteData: unknown): noteData is NoteDataWithTemplate {
  if (!noteData || typeof noteData !== 'object') {
    return false;
  }
  return 'template' in noteData;
}

// ============================================================================
// Main Extraction Functions
// ============================================================================

/**
 * Extract and normalize template info from note data
 *
 * @param noteData - Note data object that may contain template info
 * @returns Normalized template info or null
 *
 * @example
 * ```typescript
 * const templateInfo = extractTemplateInfo(noteData);
 * if (templateInfo) {
 *   console.log(templateInfo.id, templateInfo.name, templateInfo.ownerScope);
 * }
 * ```
 */
export function extractTemplateInfo(noteData: unknown): NormalizedTemplateInfo | null {
  if (!hasTemplateProperty(noteData) || !noteData.template) {
    return null;
  }

  const template = noteData.template;

  if (isValidTemplateFormat(template)) {
    return {
      id: template.template_id,
      name: template.template_name,
      ownerScope: template.owner_scope,
    };
  }

  return null;
}

// ============================================================================
// Convenience Getters
// ============================================================================

/**
 * Get template ID from note data
 *
 * @param noteData - Note data object that may contain template info
 * @returns Template ID or undefined
 *
 * @example
 * ```typescript
 * <NoteTopBar currentTemplateId={getTemplateId(noteData)} />
 * ```
 */
export function getTemplateId(noteData: unknown): string | undefined {
  const info = extractTemplateInfo(noteData);
  return info?.id;
}

/**
 * Get template name from note data
 *
 * @param noteData - Note data object that may contain template info
 * @returns Template name or undefined
 *
 * @example
 * ```typescript
 * <NoteTopBar currentTemplateName={getTemplateName(noteData)} />
 * ```
 */
export function getTemplateName(noteData: unknown): string | undefined {
  const info = extractTemplateInfo(noteData);
  return info?.name;
}

/**
 * Get template owner scope from note data
 *
 * @param noteData - Note data object that may contain template info
 * @returns Owner scope or undefined
 */
export function getTemplateOwnerScope(
  noteData: unknown,
): 'user' | 'org' | 'public' | undefined {
  const info = extractTemplateInfo(noteData);
  return info?.ownerScope;
}

